'use client';

/**
 * EmbedAccessGate — what the widget shows instead of the composer when the
 * brain's "Who can chat" setting doesn't admit this visitor (quick-info
 * `canAsk === false`). Mirrors the app's BrainQuestionComposer lock states:
 * sign in, enter the team code, or "restricted". Inline styles only (it
 * renders inside the widget's Shadow DOM).
 */

import React, { useState } from 'react';

import { widgetProxyFetch } from '../services/widgetProxy';
import { useEmbedTheme } from '../theme';

export type EmbedAskAccessReason = 'NOT_SIGNED_IN' | 'NOT_AUTHORIZED' | 'ACCESS_CODE_REQUIRED';
export type EmbedAskAccessMode = 'ANY_SIGNED_IN' | 'EMPLOYEES' | 'EMPLOYEE_GROUPS' | 'ACCESS_CODE';

interface EmbedAccessGateProps {
  brainId: string;
  brainName: string;
  reason?: string;
  mode?: string;
  /** Widget configured to hide sign-in — a visitor then has no way in */
  guestMode: boolean;
  appBaseUrl: string;
  token?: string | null;
  onSignIn: () => void;
  /** Called after a successful unlock so the brain is re-checked */
  onUnlocked: () => void;
}

export function accessGateCopy(
  reason: string | undefined,
  mode: string | undefined,
  brainName: string,
  guestMode: boolean,
): { title: string; body: string; action: 'sign-in' | 'code' | 'none' } {
  if (reason === 'ACCESS_CODE_REQUIRED') {
    return {
      title: 'Enter your access code',
      body: `${brainName} is code-protected. Enter the code you were given to start chatting.`,
      action: 'code',
    };
  }
  if (reason === 'NOT_AUTHORIZED') {
    return {
      title: 'Chat is restricted',
      body:
        mode === 'EMPLOYEE_GROUPS'
          ? `${brainName} is only open to selected teams. Ask the owner if you need access.`
          : `${brainName} is only open to the business's team. Ask the owner if you need access.`,
      action: 'none',
    };
  }
  // NOT_SIGNED_IN (or unknown): every mode needs a signed-in visitor.
  if (guestMode) {
    return {
      title: 'Not available here',
      body: `${brainName} only chats with signed-in people, and sign-in is turned off on this site.`,
      action: 'none',
    };
  }
  const bodyByMode: Record<string, string> = {
    EMPLOYEES: `${brainName} is for the team. Sign in with your work email to chat.`,
    EMPLOYEE_GROUPS: `${brainName} is for selected teams. Sign in with your work email to chat.`,
    ACCESS_CODE: `${brainName} is code-protected. Sign in, then enter the code you were given.`,
  };
  return {
    title: 'Sign in to chat',
    body: (mode && bodyByMode[mode]) || `Sign in to chat with ${brainName} — it only takes a moment.`,
    action: 'sign-in',
  };
}

export const EmbedAccessGate: React.FC<EmbedAccessGateProps> = ({
  brainId,
  brainName,
  reason,
  mode,
  guestMode,
  appBaseUrl,
  token,
  onSignIn,
  onUnlocked,
}) => {
  const { theme, primaryColor } = useEmbedTheme();
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const copy = accessGateCopy(reason, mode, brainName, guestMode);

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || submitting) return;
    setSubmitting(true);
    setCodeError(null);
    const result = await widgetProxyFetch<{ data?: { unlocked?: boolean } }>(
      appBaseUrl,
      `/asset/cognition/${brainId}/unlock`,
      { token, method: 'POST', body: { code: code.trim() } },
    );
    setSubmitting(false);
    if (result?.data?.unlocked) {
      onUnlocked();
    } else {
      setCodeError("That code didn't match. Check it and try again.");
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 16px',
    borderRadius: theme.borderRadius.lg,
    border: 'none',
    backgroundColor: primaryColor,
    color: '#ffffff',
    fontSize: theme.typography.fontSize.sm,
    fontWeight: theme.typography.fontWeight.semibold,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <div
      role="status"
      data-embed-access-gate={reason || 'locked'}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        padding: '16px',
        borderTop: `${theme.borderWidth.thin} solid ${theme.colors.border.subtle}`,
        backgroundColor: theme.colors.background.surface,
        flexShrink: 0,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke={theme.colors.text.secondary}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: '2px' }}
          aria-hidden="true"
        >
          <rect x="3" y="11" width="18" height="11" rx="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: theme.typography.fontSize.sm,
              fontWeight: theme.typography.fontWeight.semibold,
              color: theme.colors.text.primary,
            }}
          >
            {copy.title}
          </div>
          <div
            style={{
              fontSize: '13px',
              lineHeight: 1.45,
              color: theme.colors.text.secondary,
              marginTop: '2px',
            }}
          >
            {copy.body}
          </div>
        </div>
      </div>

      {copy.action === 'sign-in' && (
        <button type="button" onClick={onSignIn} style={buttonStyle}>
          Sign in
        </button>
      )}

      {copy.action === 'code' && (
        <form onSubmit={submitCode} style={{ display: 'flex', gap: '8px' }}>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. TURT-4382"
            aria-label="Access code"
            autoComplete="off"
            style={{
              flex: 1,
              minWidth: 0,
              padding: '10px 12px',
              borderRadius: theme.borderRadius.lg,
              border: `${theme.borderWidth.thin} solid ${theme.colors.border.default}`,
              backgroundColor: theme.colors.background.page,
              color: theme.colors.text.primary,
              fontSize: theme.typography.fontSize.sm,
              fontFamily: 'inherit',
              letterSpacing: '0.05em',
            }}
          />
          <button
            type="submit"
            disabled={submitting || !code.trim()}
            style={{ ...buttonStyle, opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? 'Checking…' : 'Unlock'}
          </button>
        </form>
      )}
      {codeError && (
        <div role="alert" style={{ fontSize: '12px', color: '#dc2626' }}>
          {codeError}
        </div>
      )}
    </div>
  );
};

export default EmbedAccessGate;
